import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { studentsAPI, partnersAPI, leadsAPI } from '../../services/api';
import { Spinner, Button, Paginator } from '../../components/ui';
import OutreachKitModal from './OutreachKitModal';
import toast from 'react-hot-toast';

const STATUS_OPTIONS = ['All', 'Registered', 'Contacted', 'Demo Scheduled', 'Demo Done', 'Enrolled', 'Dropped'];

const STATUS_STYLE = {
  Registered:       { bg: '#dbeafe', color: '#1d4ed8' },
  Contacted:        { bg: '#ede9fe', color: '#7c3aed' },
  'Demo Scheduled': { bg: '#fef9c3', color: '#a16207' },
  'Demo Done':      { bg: '#e0f2fe', color: '#0369a1' },
  Enrolled:         { bg: '#dcfce7', color: '#15803d' },
  Dropped:          { bg: '#fee2e2', color: '#dc2626' },
};

const SOURCE_ICON = {
  qr_code: '📷',
  whatsapp_link: '💬',
  direct_link: '🔗',
  email_link: '📧',
  manual: '✍️',
  other: '🌐',
};

const inp = { width: '100%', padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', outline: 'none', background: '#fff', boxSizing: 'border-box' };
const sel = { ...inp, cursor: 'pointer' };
const lbl = { fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 };
const row2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 };

const EMPTY_FORM = {
  full_name: '', phone: '', email: '', whatsapp: '', sameAsPhone: false,
  college_name: '', degree: '', branch: '', year_of_study: '', current_semester: '',
  course_interest: '', how_did_you_hear: '', partner_id: '', college_lead_id: '', notes: '',
};

function AddStudentModal({ onClose, onSuccess }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [partners, setPartners] = useState([]);
  const [leads, setLeads] = useState([]);
  const [dupWarning, setDupWarning] = useState(null);

  useEffect(() => {
    // Load partner profiles and institutional leads for dropdowns
    partnersAPI.getAllProfiles().then(r => setPartners(r.data?.data || [])).catch(() => {});
    leadsAPI.getAll({ limit: 100 }).then(r => {
      const institutional = (r.data?.data?.leads || []).filter(l =>
        ['Private University','Autonomous College','Regular College','Open University','Other Educational Institute'].includes(l.customer_type)
      );
      setLeads(institutional);
    }).catch(() => {});
  }, []);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = 'Required';
    if (!form.phone.trim()) e.phone = 'Required';
    return e;
  };

  const handleSameAsPhone = (checked) => {
    setF('sameAsPhone', checked);
    if (checked) setF('whatsapp', form.phone);
    else setF('whatsapp', '');
  };

  const submit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setErrors({});
    setDupWarning(null);
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.sameAsPhone;
      const res = await studentsAPI.manualAdd(payload);
      if (res.data?.duplicate_warning) {
        setDupWarning(res.data.existing_id);
        return;
      }
      toast.success('Student enquiry added successfully');
      onSuccess(res.data?.data?.student?._id);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add student');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: '#fff', borderRadius: 18, width: '100%', maxWidth: 700, maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 80px rgba(0,0,0,0.2)' }}>
        {/* Header */}
        <div style={{ padding: '18px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div>
            <h2 style={{ fontSize: 17, fontWeight: 800, color: '#1B2A6B', margin: 0 }}>✍️ Add Student Enquiry</h2>
            <p style={{ fontSize: 12, color: '#64748b', margin: '2px 0 0' }}>Manually register a student who expressed interest</p>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: '#f1f5f9', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', fontSize: 18, color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '20px 24px' }}>
          {dupWarning && (
            <div style={{ background: '#fef9c3', border: '1.5px solid #fde047', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#713f12' }}>
              ⚠️ A student with this phone number already exists.{' '}
              <Link to={`/students/${dupWarning}`} onClick={onClose} style={{ color: '#1d4ed8', fontWeight: 700 }}>View existing record →</Link>
            </div>
          )}

          <div style={{ display: 'grid', gap: 14 }}>
            {/* Row 1: Name + Phone */}
            <div style={row2}>
              <div>
                <label style={lbl}>Full Name *</label>
                <input value={form.full_name} onChange={e => setF('full_name', e.target.value)} style={{ ...inp, borderColor: errors.full_name ? '#ef4444' : '#e2e8f0' }} placeholder="e.g. Rahul Sharma" />
                {errors.full_name && <p style={{ fontSize: 11, color: '#ef4444', marginTop: 2 }}>{errors.full_name}</p>}
              </div>
              <div>
                <label style={lbl}>Phone *</label>
                <input type="tel" value={form.phone} onChange={e => { setF('phone', e.target.value); if (form.sameAsPhone) setF('whatsapp', e.target.value); }} style={{ ...inp, borderColor: errors.phone ? '#ef4444' : '#e2e8f0' }} placeholder="+91 98765 43210" />
                {errors.phone && <p style={{ fontSize: 11, color: '#ef4444', marginTop: 2 }}>{errors.phone}</p>}
              </div>
            </div>

            {/* Row 2: WhatsApp + Email */}
            <div style={row2}>
              <div>
                <label style={lbl}>
                  WhatsApp Number
                  <label style={{ fontWeight: 400, marginLeft: 8, cursor: 'pointer', color: '#64748b' }}>
                    <input type="checkbox" checked={form.sameAsPhone} onChange={e => handleSameAsPhone(e.target.checked)} style={{ marginRight: 4 }} />
                    Same as phone
                  </label>
                </label>
                <input type="tel" value={form.whatsapp} onChange={e => setF('whatsapp', e.target.value)} style={inp} placeholder="WhatsApp number" disabled={form.sameAsPhone} />
              </div>
              <div>
                <label style={lbl}>Email</label>
                <input type="email" value={form.email} onChange={e => setF('email', e.target.value)} style={inp} placeholder="student@example.com" />
              </div>
            </div>

            {/* Row 3: College + Degree */}
            <div style={row2}>
              <div>
                <label style={lbl}>College Name</label>
                <input value={form.college_name} onChange={e => setF('college_name', e.target.value)} style={inp} placeholder="e.g. JNTU Hyderabad" />
              </div>
              <div>
                <label style={lbl}>Degree</label>
                <select value={form.degree} onChange={e => setF('degree', e.target.value)} style={sel}>
                  <option value="">Select degree…</option>
                  {['B.Tech', 'BCA', 'BSc', 'MCA', 'MBA', 'Other'].map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            </div>

            {/* Row 4: Branch + Year */}
            <div style={row2}>
              <div>
                <label style={lbl}>Branch / Stream</label>
                <input value={form.branch} onChange={e => setF('branch', e.target.value)} style={inp} placeholder="e.g. Computer Science" />
              </div>
              <div>
                <label style={lbl}>Year of Study</label>
                <select value={form.year_of_study} onChange={e => setF('year_of_study', e.target.value)} style={sel}>
                  <option value="">Select year…</option>
                  {['1st', '2nd', '3rd', '4th'].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            </div>

            {/* Row 5: Semester + Course Interest */}
            <div style={row2}>
              <div>
                <label style={lbl}>Current Semester</label>
                <input type="number" min="1" max="8" value={form.current_semester} onChange={e => setF('current_semester', e.target.value)} style={inp} placeholder="1–8" />
              </div>
              <div>
                <label style={lbl}>Course Interest</label>
                <select value={form.course_interest} onChange={e => setF('course_interest', e.target.value)} style={sel}>
                  <option value="">Select…</option>
                  {['Applied AI Engineering', 'AI for Doctors', 'AI Essentials', 'Other'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            {/* Row 6: How did they hear + Partner */}
            <div style={row2}>
              <div>
                <label style={lbl}>How Did They Hear About Us</label>
                <select value={form.how_did_you_hear} onChange={e => setF('how_did_you_hear', e.target.value)} style={sel}>
                  <option value="">Select…</option>
                  {['Partner', 'Walk-in', 'Referral', 'Social Media', 'College Event', 'Other'].map(h => <option key={h} value={h}>{h}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Referred by Partner <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional)</span></label>
                <select value={form.partner_id} onChange={e => setF('partner_id', e.target.value)} style={sel}>
                  <option value="">None</option>
                  {partners.map(p => (
                    <option key={p._id || p.user_id?._id} value={p._id || p.user_id?._id}>{p.name || p.user_id?.name || 'Unknown'}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Row 7: Link to College Lead */}
            {leads.length > 0 && (
              <div>
                <label style={lbl}>Link to College Lead <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional)</span></label>
                <select value={form.college_lead_id} onChange={e => setF('college_lead_id', e.target.value)} style={sel}>
                  <option value="">None</option>
                  {leads.map(l => (
                    <option key={l._id} value={l._id}>{l.institution_name || l.type_specific_data?.institution_name || l.contact_name || l.lead_id} · {l.customer_type}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Notes */}
            <div>
              <label style={lbl}>Notes</label>
              <textarea value={form.notes} onChange={e => setF('notes', e.target.value)} rows={3} style={{ ...inp, resize: 'vertical' }} placeholder="Any additional context, interest level, follow-up notes…" />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0 }}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={saving} style={{ background: '#1B2A6B', color: '#C9A84C' }}>
            {saving ? 'Saving…' : '+ Add Student Enquiry'}
          </Button>
        </div>
      </div>
    </div>
  );
}

const PAGE_SIZE = 20;

export default function StudentListPage() {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [showOutreach, setShowOutreach] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const load = (p = 1) => {
    setLoading(true);
    const params = { page: p, limit: PAGE_SIZE };
    if (statusFilter !== 'All') params.status = statusFilter;
    if (search) params.search = search;
    Promise.all([
      studentsAPI.getAll(params),
      studentsAPI.getStats(),
    ]).then(([res, statsRes]) => {
      setStudents(res.data?.data?.students || []);
      const total = res.data?.data?.total || 0;
      setTotalPages(Math.ceil(total / PAGE_SIZE) || 1);
      setPage(p);
      setStats(statsRes.data?.data || null);
    }).catch(() => toast.error('Failed to load students'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(1); }, [statusFilter, search]);

  const handleSearchSubmit = (e) => { e.preventDefault(); setSearch(searchInput); };
  const handlePageChange = (p) => { load(p); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const handleStudentAdded = (newId) => {
    setShowAddModal(false);
    if (newId) navigate(`/students/${newId}`);
    else load();
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: '#1B2A6B', margin: 0 }}>Student Registrations</h1>
          <p style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>Manage all inbound student leads from your funnel</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="primary" size="sm" onClick={() => setShowAddModal(true)}
            style={{ background: '#1B2A6B', color: '#C9A84C', fontWeight: 700 }}>
            + Add Student Enquiry
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShowOutreach(true)}>
            📤 Outreach Kit
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10, marginBottom: 20 }}>
          {[
            { label: 'Total', value: stats.total, color: '#1B2A6B' },
            { label: 'Registered', value: stats.by_status?.Registered || 0, color: '#1d4ed8' },
            { label: 'Demo Done', value: stats.by_status?.['Demo Done'] || 0, color: '#0369a1' },
            { label: 'Enrolled', value: stats.by_status?.Enrolled || 0, color: '#15803d' },
            { label: 'Dropped', value: stats.by_status?.Dropped || 0, color: '#dc2626' },
          ].map(s => (
            <div key={s.label} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '12px 14px', textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Funnel bar */}
      {stats && stats.total > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '12px 16px', marginBottom: 20 }}>
          <p style={{ fontSize: 11, fontWeight: 700, color: '#64748b', margin: '0 0 8px' }}>FUNNEL OVERVIEW</p>
          <div style={{ display: 'flex', height: 12, borderRadius: 6, overflow: 'hidden', background: '#f1f5f9', gap: 1 }}>
            {['Registered', 'Contacted', 'Demo Scheduled', 'Demo Done', 'Enrolled'].map((s, i) => {
              const count = stats.by_status?.[s] || 0;
              const pct = stats.total > 0 ? (count / stats.total) * 100 : 0;
              const colors = ['#93c5fd', '#c4b5fd', '#fde68a', '#7dd3fc', '#86efac'];
              return pct > 0 ? (
                <div key={s} title={`${s}: ${count}`} style={{ width: `${pct}%`, background: colors[i], minWidth: 4, transition: 'width 0.5s' }} />
              ) : null;
            })}
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
            {['Registered', 'Contacted', 'Demo Scheduled', 'Demo Done', 'Enrolled'].map((s, i) => {
              const colors = ['#93c5fd', '#c4b5fd', '#fde68a', '#7dd3fc', '#86efac'];
              const count = stats.by_status?.[s] || 0;
              return (
                <span key={s} style={{ fontSize: 11, color: '#64748b', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: colors[i], display: 'inline-block' }} />
                  {s} ({count})
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {STATUS_OPTIONS.map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              style={{
                padding: '6px 12px', borderRadius: 20, border: '1.5px solid',
                fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
                background: statusFilter === s ? '#1B2A6B' : '#fff',
                color: statusFilter === s ? '#fff' : '#64748b',
                borderColor: statusFilter === s ? '#1B2A6B' : '#e2e8f0',
              }}>
              {s}
            </button>
          ))}
        </div>

        <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
          <input
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder="Search name, email, phone…"
            style={{ padding: '7px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none', width: 220 }}
          />
          <Button type="submit" variant="ghost" size="sm">Search</Button>
          {search && <Button variant="ghost" size="sm" onClick={() => { setSearch(''); setSearchInput(''); }}>Clear</Button>}
        </form>
      </div>

      {/* List */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spinner /></div>
      ) : students.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#94a3b8' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🎓</div>
          <p style={{ fontSize: 15, fontWeight: 600 }}>No students found</p>
          <p style={{ fontSize: 13 }}>Add a student enquiry or share your Outreach Kit to get registrations</p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12 }}>
            <Button variant="primary" size="sm" onClick={() => setShowAddModal(true)} style={{ background: '#1B2A6B', color: '#C9A84C' }}>
              + Add Student Enquiry
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setShowOutreach(true)}>
              📤 Get Outreach Kit
            </Button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {students.map(s => {
            const ss = STATUS_STYLE[s.status] || STATUS_STYLE.Registered;
            return (
              <div key={s._id}
                onClick={() => navigate(`/students/${s._id}`)}
                style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 16px', cursor: 'pointer', transition: 'box-shadow 0.15s', display: 'flex', alignItems: 'center', gap: 14 }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
              >
                <div style={{ width: 42, height: 42, borderRadius: '50%', background: 'linear-gradient(135deg, #1B2A6B, #2d3f8a)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 16, flexShrink: 0 }}>
                  {s.name.charAt(0).toUpperCase()}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, color: '#1e293b', fontSize: 14 }}>{s.name}</span>
                    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 700, background: ss.bg, color: ss.color }}>{s.status}</span>
                    {s.scholarship_test_taken && <span style={{ fontSize: 11, background: '#dcfce7', color: '#15803d', padding: '2px 7px', borderRadius: 10, fontWeight: 700 }}>🎓 Test Taken</span>}
                    {s.lead_id && <span style={{ fontSize: 11, background: '#e0f2fe', color: '#0369a1', padding: '2px 7px', borderRadius: 10, fontWeight: 700 }}>→ Lead</span>}
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                    {s.phone} · {s.email}
                    {s.college && ` · ${s.college}`}
                    {s.course && ` · ${s.course}`}
                  </div>
                </div>

                <div style={{ textAlign: 'right', flexShrink: 0, fontSize: 12, color: '#94a3b8' }}>
                  <div>{SOURCE_ICON[s.registration_source] || '🔗'} {s.registration_source?.replace('_', ' ')}</div>
                  {s.interest_area && <div style={{ marginTop: 2, color: '#64748b', fontWeight: 600 }}>{s.interest_area}</div>}
                  <div style={{ marginTop: 2 }}>{new Date(s.createdAt).toLocaleDateString('en-IN')}</div>
                  {s.partner_id && <div style={{ color: '#94a3b8' }}>via {s.partner_id.name}</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Paginator page={page} totalPages={totalPages} onPageChange={handlePageChange} />

      {showOutreach && (
        <OutreachKitModal
          onClose={() => setShowOutreach(false)}
          onAddStudent={() => { setShowOutreach(false); setShowAddModal(true); }}
        />
      )}
      {showAddModal && (
        <AddStudentModal onClose={() => setShowAddModal(false)} onSuccess={handleStudentAdded} />
      )}
    </div>
  );
}
